import { useCallback, useEffect, useId } from 'react';
import type { RefObject } from 'react';

const modalStack: string[] = [];

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const isTopmostModal = (id: string): boolean =>
  modalStack[modalStack.length - 1] === id;

interface UseModalFocusTrapResult {
  modalId: string;
  isTopmost: () => boolean;
}

export const useModalFocusTrap = (
  modalRef: RefObject<HTMLElement | null>,
  // Popovers that portal outside the modal DOM (e.g. a filters dropdown) must
  // only claim the trap while open. Registering an always-on trap would either
  // sit inertly on the stack or — once opened — lose "topmost" to the parent
  // modal, whose trap then yanks focus back out of the portaled popover.
  enabled = true
): UseModalFocusTrapResult => {
  const modalId = useId();
  const isTopmost = useCallback(
    (): boolean => isTopmostModal(modalId),
    [modalId]
  );

  useEffect(() => {
    if (!enabled) return undefined;

    modalStack.push(modalId);

    return () => {
      const idx = modalStack.indexOf(modalId);

      if (idx >= 0) modalStack.splice(idx, 1);
    };
  }, [modalId, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    const handleTabTrap = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;
      if (!isTopmostModal(modalId)) return;
      const modalEl = modalRef.current;

      if (!modalEl) return;

      const focusable = Array.from(
        modalEl.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter(
        (el) =>
          // Skip elements inside an inert subtree (e.g. a collapsed section):
          // they are visually present but removed from the tab order.
          !el.closest('[inert]') &&
          (el.offsetParent !== null || el === document.activeElement)
      );

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const activeInsideModal = active != null && modalEl.contains(active);

      if (e.shiftKey && (active === first || !activeInsideModal)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !activeInsideModal)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleTabTrap, true);

    return () => {
      document.removeEventListener('keydown', handleTabTrap, true);
    };
  }, [modalId, modalRef, enabled]);

  return {
    modalId,
    isTopmost,
  };
};
